import {
  Component, Output, EventEmitter, signal, computed, inject,
  ElementRef, ViewChild, HostListener
} from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { toSignal } from '@angular/core/rxjs-interop';
import { AppState } from '../../../core/store';
import { selectCatalogProducts } from '../../../core/store/catalog/catalog.selectors';
import { UiActions, RecentlyViewedItem } from '../../../core/store/ui/ui.actions';
import { selectRecentSearches, selectRecentlyViewed } from '../../../core/store/ui/ui.selectors';
import { CatalogProductResponse } from '../../../core/models/product-catalog.model';
import { ProviderLogo } from '../provider-logo/provider-logo';
import { Icon } from '../icon/icon';
import { CatalogNavSourceService } from '../../services/catalog-nav-source.service';
import { getDomainLabel } from '../../../core/utils/product-tag-mappings';
import { extractSearchText } from '../../../core/utils/extract-search-text';
import { GoogleAnalyticsService } from '../../../core/services/google-analytics.service';

const PLATFORM_LABEL_MAP: Record<string, string> = {
  petrel:                    'Petrel',
  techlog:                   'Techlog',
  studiomanager:             'Studio',
  drillplan:                 'DrillPlan',
  drillops:                  'DrillOps',
  exploreplan:               'ExplorePlan',
  fdplan:                    'FDPlan',
  gaia:                      'GAIA',
  prodops:                   'ProdOps',
  tela:                      'Tela',
  lumi:                      'Lumi',
  'lumi-ai-workspace':       'Lumi AI Workspace',
  'lumi-tools':              'Lumi Tools',
  'sustainability-platform-app':'Sustainability Platform App',
};

const DOMAIN_ICON_PATH: Record<string, string> = {
  'Subsurface':        'M9 0v20H0V0h9Zm6.004 0L20 7l-3.747 3L20 13l-4.996 7L10 13l3.753-3L10 7l5.004-7ZM7.5 13.75h-6v4.75h6v-4.75Zm7.454-2.79-2.907 2.324 2.956 4.136 2.951-4.136-2.901-2.323-.049.039-.05-.04ZM7.5 7.75h-6v4.5h6v-4.5Zm7.503-5.17-2.956 4.136 2.907 2.324.05-.04.049.039 2.901-2.323-2.951-4.136ZM7.5 1.5h-6v4.75h6V1.5Z',
  'Production':        'M10 0C4.477 0 0 4.477 0 10s4.477 10 10 10 10-4.477 10-10S15.523 0 10 0zm0 1.5a8.5 8.5 0 1 1 0 17 8.5 8.5 0 0 1 0-17zm0 2a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13zm0 2a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9zm0 2a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5z',
  'Well Construction': 'M16.598 5.613h-2.26V4.215L14.347 0H6.08l-.004 5.613H3.873C2.826 5.613 2 6.493 2 7.536V17.2L4.7 20l2.698-2.745 2.7 2.745 2.755-2.745L15.606 20l2.809-2.8V7.536c.056-1.098-.77-1.923-1.817-1.923zM12.843 1.5l-.004 2.715V5.61H7.576L7.58 1.5h5.264zm4.072 5.998v9.079l-1.309 1.305-2.753-2.745-2.745 2.735-2.71-2.757L4.71 17.85 3.5 16.595V7.536c0-.229.17-.423.373-.423h12.725c.102 0 .186.032.242.091.056.06.083.146.077.256l-.002.038z',
  'Sustainability':    'M13.2 0c-.2.5-.4 1-.7 1.4-1.2 1.7-3.1 2.5-5 3.2-1.1.4-2.2.9-3.1 1.5C.3 9 .2 13.6 2.5 16.1c.2-.6.4-1.2.6-1.7-.2-.4-.4-.8-.5-1.2-.4-1.8.2-4.1 2.6-5.8.6-.4 1.3-.8 2-1l.8-.3c.6-.2 1.3-.5 1.9-.8 1.4-.6 2.6-1.5 3.6-2.7 2.1 2.7 3.3 6.1 3 8.7-.1 1.5-1 2.9-2.3 3.7-1.4.9-3.6 1.6-7.1 1.5-.2.4-.3.9-.4 1.5 14.4.5 12.8-10.3 7.6-16.7-.4-.4-.7-.9-1.1-1.2Zm-1.4 6.7c-.9 1.9-2 2.7-3.2 3.6-.4.3-.9.6-1.3 1-1.1.8-1.9 1.9-2.5 3.1-.8 1.8-1.3 3.7-1.4 5.6h1.5c.3-2.2.7-3.8 1.3-4.9.5-1 1.2-1.9 2.1-2.6.4-.3.7-.6 1.1-.8 1.2-.9 2.6-1.9 3.7-3.8L12 6.8Z',
  'Data Management':   'M19.986 3.435L20 3.332l-.014-.103C19.776 1.669 15.713 0 10 0 4.287 0 .224 1.67.014 3.228L0 3.332l.014.102c.007.053.03.108.047.162H0v14.139l.457.203C3.563 19.313 6.72 20 9.908 20c3.183 0 6.398-.686 9.623-2.057l.469-.199V3.467h-.023c.002-.01.008-.022.01-.032zM1.542 9.573c2.755 1.073 5.548 1.613 8.368 1.613 2.83 0 5.685-.545 8.548-1.63v2.755c-5.725 2.311-11.41 2.313-16.916.008V9.573zm0-1.657V5.021C3.252 5.949 6.27 6.662 10 6.662c3.73 0 6.749-.713 8.459-1.641v2.887c-5.726 2.313-11.41 2.313-16.917.008zM10 1.537c4.96 0 7.68 1.277 8.326 1.767-.699.569-3.414 1.821-8.326 1.821-4.96 0-7.68-1.277-8.326-1.768.699-.568 3.414-1.82 8.326-1.82zM1.542 16.731v-2.755c2.754 1.073 5.547 1.612 8.366 1.612 2.83 0 5.685-.545 8.55-1.629v2.765c-5.725 2.311-11.41 2.311-16.916.007z',
};

// Label = display copy; q = actual search term sent to catalog (and used for count).
// Kept separate because marketing labels like "Petrel plugins" use words that don't
// appear verbatim in product descriptions — the effective search term does.
const POPULAR_QUERIES: { label: string; q: string }[] = [
  { label: 'Petrel plugins',        q: 'petrel'       },
  { label: 'Seismic interpretation', q: 'seismic'      },
  { label: 'Reservoir simulation',  q: 'reservoir'    },
  { label: 'Well log analysis',     q: 'well log'     },
];

export interface SearchResult {
  id: string;
  name: string;
  provider: string;
  domain: string;
  platform: string;
}

export interface CatGroup {
  name: string;
  matches: number;
}

@Component({
  selector: 'app-search-panel',
  standalone: true,
  imports: [ProviderLogo, Icon],
  templateUrl: './search-panel.html',
  styleUrl: './search-panel.scss',
})
export class SearchPanel {
  @ViewChild('queryInput') queryInput!: ElementRef<HTMLInputElement>;
  @Output() closed = new EventEmitter<void>();

  private store        = inject(Store<AppState>);
  private router       = inject(Router);
  private catalogNavSource = inject(CatalogNavSourceService);
  private readonly ga  = inject(GoogleAnalyticsService);

  private catalogProducts = toSignal(
    this.store.select(selectCatalogProducts),
    { initialValue: [] as CatalogProductResponse[] }
  );

  isOpen     = signal(false);
  query      = signal('');
  activeIdx  = signal(-1);

  recentSearches  = toSignal(this.store.select(selectRecentSearches), { initialValue: [] as string[] });
  recentlyViewed = toSignal(this.store.select(selectRecentlyViewed), { initialValue: [] as RecentlyViewedItem[] });

  popularSearches = computed(() =>
    POPULAR_QUERIES.map(({ label, q }) => ({
      label,
      q,
      count: this.catalogProducts().filter(p => this.matches(p, q)).length,
    }))
  );

  // ─── Derivation helpers ───────────────────────────────────────────

  private domainLabel(domains?: string[]): string {
    return getDomainLabel(domains?.map(d => d.toUpperCase()));
  }

  private platformLabel(p: CatalogProductResponse): string {
    if (p.additionalPlatformTypes?.length) {
      return p.additionalPlatformTypes.map(pt => PLATFORM_LABEL_MAP[pt.toLowerCase()] ?? pt).join(' · ');
    }
    return PLATFORM_LABEL_MAP[(p.platformType ?? '').toLowerCase()] ?? p.platformType ?? '';
  }

  private matches(p: CatalogProductResponse, q: string): boolean {
    const STOP = new Set(['for', 'and', 'the', 'in', 'of', 'to', 'a', 'an', 'or', 'with', 'by']);
    const words = q.toLowerCase().split(/\s+/).filter(w => w.length > 1 && !STOP.has(w));
    if (!words.length) return false;
    const haystack = [
      p._searchText ?? extractSearchText(p),
      this.domainLabel(p.domains),
      this.platformLabel(p),
    ].join(' ').toLowerCase();
    return words.every(word => haystack.includes(word));
  }

  // ─── Computed signals ─────────────────────────────────────────────

  queryHints = computed<string[]>(() => {
    const q = this.query().trim();
    if (!q) return [];
    return [`${q} for Petrel`, `${q} agent`, `${q} plugin`, `${q} connector`];
  });

  private matchedProducts = computed(() => {
    const q = this.query().trim();
    if (!q) return [];
    return this.catalogProducts().filter(p => this.matches(p, q));
  });

  solutions = computed<SearchResult[]>(() =>
    this.matchedProducts().slice(0, 4).map(p => ({
      id:       p.code,
      name:     p.name,
      provider: p.vendorName,
      domain:   this.domainLabel(p.domains),
      platform: this.platformLabel(p),
    }))
  );

  categories = computed<{ domains: CatGroup[]; products: CatGroup[]; providers: CatGroup[]; tela: number }>(() => {
    const matched = this.matchedProducts();
    if (!matched.length) return { domains: [], products: [], providers: [], tela: 0 };

    const tally = (getKey: (p: CatalogProductResponse) => string): CatGroup[] => {
      const counts: Record<string, number> = {};
      for (const p of matched) {
        const key = getKey(p);
        if (key) counts[key] = (counts[key] ?? 0) + 1;
      }
      return Object.entries(counts)
        .map(([name, matches]) => ({ name, matches }))
        .sort((a, b) => b.matches - a.matches)
        .slice(0, 4);
    };

    return {
      domains:   tally(p => this.domainLabel(p.domains)),
      products:  tally(p => this.platformLabel(p)),
      providers: tally(p => p.vendorName),
      tela:      matched.filter(p => p.additionalPlatformTypes?.includes('tela') || (p.platformType ?? '').toLowerCase() === 'tela').length,
    };
  });

  hasCategories = computed(() => {
    const c = this.categories();
    return c.domains.length + c.products.length + c.providers.length + c.tela > 0;
  });

  hasContent = computed(() => this.queryHints().length > 0 || this.solutions().length > 0);

  // ─── Public API ───────────────────────────────────────────────────

  open(): void {
    this.isOpen.set(true);
    this.query.set('');
    this.activeIdx.set(-1);
    requestAnimationFrame(() => this.queryInput?.nativeElement.focus());
  }

  close(): void {
    this.isOpen.set(false);
    this.query.set('');
    this.closed.emit();
  }

  // ─── Event handlers ───────────────────────────────────────────────

  onOverlayClick(event: MouseEvent): void {
    if ((event.target as Element).classList.contains('sp-overlay')) this.close();
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (this.isOpen() && event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }

  onInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.goToSearch();
    }
  }

  goToSearch(q?: string, source: 'typed' | 'hint' | 'recent' | 'popular' = 'typed'): void {
    const term = (q ?? this.query()).trim();
    if (term) {
      const resultCount = this.matchedProducts().length;
      this.ga.trackEvent('search_submit', { search_term: term, result_count: resultCount, source });
      if (resultCount === 0) {
        this.ga.trackEvent('search_no_results', { search_term: term, source });
      }
      this.store.dispatch(UiActions.addRecentSearch({ query: term }));
      this.catalogNavSource.source.set('search-panel');
      this.router.navigate(['/catalog'], { queryParams: { search: term } });
    } else {
      this.router.navigate(['/catalog']);
    }
    this.close();
  }

  goToCategory(type: 'domain' | 'product' | 'provider' | 'tela', value?: string): void {
    const q = this.query().trim();
    if (q) this.store.dispatch(UiActions.addRecentSearch({ query: q }));
    this.ga.trackEvent('search_category_click', {
      search_term: q,
      category_type: type,
      category_value: value ?? 'Tela',
    });
    const params: Record<string, string> =
      type === 'domain'   ? { domain: value! } :
      type === 'product'  ? { product: value! } :
      type === 'provider' ? { provider: value! } :
                            { filter: 'Tela' };
    this.catalogNavSource.source.set('search-panel');
    this.router.navigate(['/catalog'], { queryParams: params });
    this.close();
  }

  domainIconPath(name: string): string {
    return DOMAIN_ICON_PATH[name] ?? '';
  }

  goToProduct(sol: SearchResult, index: number): void {
    this.ga.trackEvent('search_result_click', {
      search_term: this.query().trim(),
      product_id: sol.id,
      product_name: sol.name,
      result_position: index + 1,
    });
    this.store.dispatch(UiActions.addRecentlyViewed({
      product: {
        id: sol.id,
        name: sol.name,
        provider: sol.provider,
        domain: sol.domain || undefined,
        platform: sol.platform || undefined,
      },
    }));
    const q = this.query().trim();
    this.router.navigate(['/product', sol.id], { queryParams: { from: 'search', q } });
    this.close();
  }

  goToRecentlyViewed(item: RecentlyViewedItem): void {
    this.store.dispatch(UiActions.addRecentlyViewed({ product: item }));
    this.router.navigate(['/product', item.id]);
    this.close();
  }

  removeRecentSearch(event: MouseEvent, query: string): void {
    event.stopPropagation();
    this.store.dispatch(UiActions.removeRecentSearch({ query }));
  }

  clearQuery(): void {
    this.query.set('');
    this.queryInput?.nativeElement.focus();
  }

  // ─── Utilities ────────────────────────────────────────────────────

  highlight(text: string, query: string): { text: string; match: boolean }[] {
    if (!query || !text) return [{ text, match: false }];
    const idx = text.toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1) return [{ text, match: false }];
    return [
      { text: text.slice(0, idx),              match: false },
      { text: text.slice(idx, idx + query.length), match: true  },
      { text: text.slice(idx + query.length),  match: false },
    ];
  }
}
